import { PaginatedResponse } from '@gorila-shared-ui/components';
import { atom, selector, selectorFamily } from 'recoil';
import { Place, PlaceCore } from '../types/place';

export const loadingPlacesState = atom({
  key: 'app.client.places.loading',
  default: false,
});

export const placesState = atom<PaginatedResponse<Place> | undefined>({
  key: 'app.client.places',
  default: undefined,
});

export const placeIdsState = selector<string[] | undefined>({
  key: 'app.client.placeIds',
  get: ({ get }) => {
    const places = get(placesState);
    if (!places) return;
    return places.items.map((place) => place._id);
  },
});

export const placeState = selectorFamily<Place | undefined, string>({
  key: 'app.client.place',
  get:
    (id) =>
    ({ get }) => {
      const places = get(placesState);
      if (!places) return;
      return places.items.find((place) => place._id === id);
    },
});

export const selectedPlaceIdState = atom<string | undefined>({
  key: 'app.client.places.selectedId',
  default: undefined,
});

export const searchPlaceState = atom<string | undefined>({
  key: 'app.client.places.search',
  default: undefined,
});

export const placesPageState = atom({
  key: 'app.client.places.page',
  default: 1,
});

export const selectedPlaceState = selector({
  key: 'app.client.places.selected',
  get: ({ get }) => {
    const selectedPlaceId = get(selectedPlaceIdState);
    if (!selectedPlaceId) return;
    return get(placeState(selectedPlaceId));
  },
});

export const editSelectedPlaceState = atom({
  key: 'app.client.places.selected.edit',
  default: false,
});

export const editingPlaceState = atom<PlaceCore | undefined>({
  key: 'app.client.places.editing',
  default: selector({
    key: 'app.client.places.editing.initializer',
    get: ({ get }) => {
      const selectedPlaceId = get(selectedPlaceIdState);
      if (selectedPlaceId === undefined) return;
      if (selectedPlaceId === '') {
        return {
          _id: '',
          address: '',
        };
      }
      return get(placeState(selectedPlaceId));
    },
  }),
});

export const updatePlacesState = atom<string | undefined>({
  key: 'app.client.places.update',
  default: undefined,
});

export const placesGeojsonState = selector<GeoJSON.FeatureCollection | undefined>({
  key: 'app.places.geojson',
  get: ({ get }) => {
    const places = get(placesState);
    if (!places?.items.length) return;
    const selectedPlaceId = get(selectedPlaceIdState);

    let filteredPlaces = places.items.filter((place) => !!place.geoPoint);

    if (selectedPlaceId) {
      filteredPlaces = filteredPlaces.filter((place) => place._id !== selectedPlaceId);
    }

    return {
      type: 'FeatureCollection',
      features: filteredPlaces.map((place) => ({
        type: 'Feature',
        geometry: place.geoPoint!,
        properties: {
          name: place.uuid,
        },
      })),
    };
  },
});
